import { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { IFilterItem } from '../../data-types/filterItem';

export interface IReportsTableFilteringValues {
    folderFilterName: string;
    entityFilterName: string;
    jurisdictionFilterName: string;
    periodFilterName: string;
    caseFilterName: string;

    clearAllFilters: () => void;

    columnFilters: Record<string, IFilterItem[]>;
    setColumnFilters: Dispatch<SetStateAction<Record<string, IFilterItem[]>>>;

    checkedPeriodItems: IFilterItem[];
    setCheckedPeriodItems: (items: IFilterItem[]) => void;
    checkedEntityItems: IFilterItem[];
    setCheckedEntityItems: (items: IFilterItem[]) => void;
    checkedEntityGroupItems: IFilterItem[];
    setCheckedEntityGroupItems: (items: IFilterItem[]) => void;
    checkedCaseItems: IFilterItem[];
    setCheckedCaseItems: (items: IFilterItem[]) => void;
    checkedJurisdictionItems: IFilterItem[];
    setCheckedJurisdictionItems: (items: IFilterItem[]) => void;
    selectedFolderFilter: IFilterItem | null | undefined;
    setSelectedFolderFilter: (filter: IFilterItem | null) => void;

    searchEntityValue: string;
    setSearchEntityValue: (value: string) => void;
    searchJurisdictionValue: string;
    setSearchJurisdictionValue: (value: string) => void;
    searchPeriodValue: string;
    setSearchPeriodValue: (value: string) => void;
    searchCaseValue: string;
    setSearchCaseValue: (value: string) => void;
}

export const ReportsTableFilteringContext = createContext<IReportsTableFilteringValues | null>(null);

export function ReportsTableFilteringContextProvider(props: { value?: IReportsTableFilteringValues; children?: React.ReactNode }) {
    const folderFilterName = 'folderName';
    const entityFilterName = 'entityName';
    const jurisdictionFilterName = 'jurisdiction';
    const periodFilterName = 'period';
    const caseFilterName = 'case';

    const [columnFilters, setColumnFilters] = useState<Record<string, IFilterItem[]>>({});

    const [checkedPeriodItems, setCheckedPeriodItems] = useState<IFilterItem[]>([]);
    const [checkedEntityItems, setCheckedEntityItems] = useState<IFilterItem[]>([]);
    const [checkedEntityGroupItems, setCheckedEntityGroupItems] = useState<IFilterItem[]>([]);
    const [checkedCaseItems, setCheckedCaseItems] = useState<IFilterItem[]>([]);
    const [checkedJurisdictionItems, setCheckedJurisdictionItems] = useState<IFilterItem[]>([]);
    const [selectedFolderFilter, setSelectedFolderFilter] = useState<IFilterItem | null>();

    const [searchEntityValue, setSearchEntityValue] = useState<string>('');
    const [searchJurisdictionValue, setSearchJurisdictionValue] = useState<string>('');
    const [searchPeriodValue, setSearchPeriodValue] = useState<string>('');
    const [searchCaseValue, setSearchCaseValue] = useState<string>('');

    const clearAllFilters = () => {
        setCheckedEntityItems([]);
        setCheckedEntityGroupItems([]);
        setCheckedJurisdictionItems([]);
        setCheckedPeriodItems([]);
        setCheckedCaseItems([]);
        setSelectedFolderFilter(null);

        setSearchCaseValue('');
        setSearchEntityValue('');
        setSearchJurisdictionValue('');
        setSearchPeriodValue('');
    };

    useEffect(() => {
        let allEntityFilters: IFilterItem[] = [];
        if (checkedEntityItems?.length) {
            allEntityFilters = allEntityFilters.concat(checkedEntityItems);
        }
        if (checkedEntityGroupItems?.length) {
            allEntityFilters = allEntityFilters.concat(checkedEntityGroupItems);
        }
        const columnFilters = {
            [folderFilterName]: selectedFolderFilter ? [selectedFolderFilter] : [],
            [entityFilterName]: allEntityFilters,
            [jurisdictionFilterName]: checkedJurisdictionItems,
            [periodFilterName]: checkedPeriodItems,
            [caseFilterName]: checkedCaseItems,
        };
        setColumnFilters(columnFilters);
    }, [
        checkedEntityGroupItems,
        checkedCaseItems,
        checkedEntityItems,
        checkedJurisdictionItems,
        checkedPeriodItems,
        selectedFolderFilter,
        setColumnFilters,
    ]);

    return (
        <ReportsTableFilteringContext.Provider
            value={
                props.value ?? {
                    folderFilterName,
                    entityFilterName,
                    jurisdictionFilterName,
                    periodFilterName,
                    caseFilterName,
                    clearAllFilters,
                    columnFilters,
                    setColumnFilters,
                    checkedPeriodItems,
                    setCheckedPeriodItems,
                    checkedEntityItems,
                    setCheckedEntityItems,
                    checkedEntityGroupItems,
                    setCheckedEntityGroupItems,
                    checkedCaseItems,
                    setCheckedCaseItems,
                    checkedJurisdictionItems,
                    setCheckedJurisdictionItems,
                    selectedFolderFilter,
                    setSelectedFolderFilter,
                    searchEntityValue,
                    setSearchEntityValue,
                    searchJurisdictionValue,
                    setSearchJurisdictionValue,
                    searchPeriodValue,
                    setSearchPeriodValue,
                    searchCaseValue,
                    setSearchCaseValue,
                }
            }
        >
            {props.children}
        </ReportsTableFilteringContext.Provider>
    );
}
